import React, { useState } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import logo from '../Assets/Logo.png';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';
import useLoginIA from '../Hooks/LoginHooks/useLoginIA';
import usePasswordToggle from '../Hooks/LoginHooks/usePasswordToggle';

const GlobalStyle = createGlobalStyle`
  @keyframes shimmer {
    0% { transform: translateX(-100%) rotate(25deg); }
    100% { transform: translateX(100%) rotate(25deg); }
  }
`;

const LoginIA = ({ embedded = false, onBackToMainLogin }) => {
  const {
    code,
    setCode,
    password,
    setPassword,
    error,
    setError,
    loading,
    handleLogin,
  } = useLoginIA();

  const { showPassword, togglePasswordVisibility } = usePasswordToggle();
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleBackToMainLogin = () => {
    if (typeof onBackToMainLogin === 'function') {
      onBackToMainLogin();
      return;
    }

    navigate('/Home');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await handleLogin(e);
    } catch (err) {
      setError('Invalid IA credentials. Please try again.');
      toast.error('Invalid IA credentials. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <GlobalStyle />
      {!embedded && <ToastContainer />}
      {embedded ? (
        <AuthSurface $embedded>
          <RightContainer>
            <CurvedTop />
            <CloseButton onClick={onBackToMainLogin}>&times;</CloseButton>
            <LogoWrapper>
              <LogoFlip>
                <LogoFace src={logo} alt="Logo Front" />
                <LogoBack src={logo} alt="Logo Back" />
              </LogoFlip>
            </LogoWrapper>
            <LoginTitle>IA PORTAL</LoginTitle>

            <SignInForm onSubmit={handleSubmit}>
              <FormField>
                <InputLabel>IA Code</InputLabel>
                <Input
                  type="number"
                  placeholder="Enter your IA code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  required
                />
              </FormField>

              <FormField>
                <InputLabel>Password</InputLabel>
                <PasswordInputWrapper>
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <PasswordToggleIcon onClick={togglePasswordVisibility}>
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </PasswordToggleIcon>
                </PasswordInputWrapper>
              </FormField>

              <ForgotPasswordLink href="#">Forgot Password?</ForgotPasswordLink>

              {error && <ErrorBox>{error}</ErrorBox>}

              <SubmitButton type="submit" disabled={loading || isLoading}>
                {loading || isLoading ? 'Signing in...' : 'Log In'}
              </SubmitButton>

              <BackButton type="button" onClick={handleBackToMainLogin}>
                Back to Main Portal
              </BackButton>

              {(isLoading || loading) && <Overlay />}
            </SignInForm>
          </RightContainer>
        </AuthSurface>
      ) : (
        <PageShell>
          <AuthSurface>
            <RightContainer>
              <CurvedTop />
              <CloseButton onClick={() => navigate('/Home')}>&times;</CloseButton>
              <LogoWrapper>
                <LogoFlip>
                  <LogoFace src={logo} alt="Logo Front" />
                  <LogoBack src={logo} alt="Logo Back" />
                </LogoFlip>
              </LogoWrapper>
              <LoginTitle>IA LOGIN</LoginTitle>

              <SignInForm onSubmit={handleSubmit}>
                <FormField>
                  <InputLabel>IA Code</InputLabel>
                  <Input
                    type="number"
                    placeholder="Enter your IA code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    required
                  />
                </FormField>

                <FormField>
                  <InputLabel>Password</InputLabel>
                  <PasswordInputWrapper>
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                    <PasswordToggleIcon onClick={togglePasswordVisibility}>
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </PasswordToggleIcon>
                  </PasswordInputWrapper>
                </FormField>

                <ForgotPasswordLink href="#">Forgot Password?</ForgotPasswordLink>

                {error && <ErrorBox>{error}</ErrorBox>}

                <SubmitButton type="submit" disabled={loading || isLoading}>
                  {loading || isLoading ? 'Signing in...' : 'Log In'}
                </SubmitButton>

                <BackButton type="button" onClick={handleBackToMainLogin}>
                  Back to Main Portal
                </BackButton>

                {(isLoading || loading) && <Overlay />}
              </SignInForm>
            </RightContainer>
          </AuthSurface>
        </PageShell>
      )}
    </>
  );
};

export default LoginIA;

const PageShell = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background:
    radial-gradient(circle at top left, rgba(0, 255, 127, 0.18), transparent 32%),
    radial-gradient(circle at bottom right, rgba(24, 89, 29, 0.22), transparent 34%),
    linear-gradient(135deg, #071b0f 0%, #0f3d1d 45%, #f4f8f5 45%, #ffffff 100%);
`;

const AuthSurface = styled.div`
  width: min(560px, 100%);
  min-height: 660px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.94);
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.25);

  ${({ $embedded }) =>
    $embedded &&
    `
      width: 100%;
      min-height: 100%;
      box-shadow: none;
      border-radius: 0;
      background: transparent;
    `}
`;

const RightContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 100%;
  padding: 30px 20px 20px;
  position: relative;
`;

const CurvedTop = styled.div`
  position: absolute;
  top: 0;
  width: 100%;
  height: 150px;
  background: linear-gradient(45deg, #18591d, #00ff7f);
  border-bottom-left-radius: 50% 75%;
  border-bottom-right-radius: 50% 75%;
  overflow: hidden;
  z-index: 0;

  &::after {
    content: '';
    position: absolute;
    top: -50%;
    left: -75%;
    width: 200%;
    height: 200%;
    background: linear-gradient(120deg, transparent 30%, rgba(255, 255, 255, 0.3) 50%, transparent 70%);
    transform: rotate(25deg);
    animation: shimmer 3s linear infinite;
  }
`;

const CloseButton = styled.span`
  position: absolute;
  top: 20px;
  right: 20px;
  font-size: 2.5rem;
  color: green;
  cursor: pointer;
  z-index: 2;
`;

const LogoWrapper = styled.div`
  perspective: 1000px;
  width: 80px;
  height: 80px;
  margin-top: -9px;
  z-index: 2;
`;

const Logo = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: 20px;
`;

const LogoFlip = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  transform-style: preserve-3d;
  animation: flipLogo 6s linear infinite;

  @keyframes flipLogo {
    0% { transform: rotateY(0deg); }
    100% { transform: rotateY(360deg); }
  }
`;

const LogoFace = styled.img`
  position: absolute;
  width: 100%;
  height: 100%;
  backface-visibility: hidden;
  border-radius: 50%;
`;

const LogoBack = styled(LogoFace)`
  transform: rotateY(180deg);
`;

const LoginTitle = styled.h2`
  position: absolute;
  top: 110px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 1.5rem;
  font-weight: 700;
  color: white;
  z-index: 2;
`;

const SignInForm = styled.form`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 80%;
  margin-top: 90px;
  gap: 15px;
`;

const FormField = styled.div`
  margin: 15px 0 10px;
  width: 100%;
`;

const InputLabel = styled.label`
  font-size: 0.9rem;
  font-weight: bold;
  margin-bottom: 5px;
  display: block;
  color: #000;
`;

const BackButton = styled.button`
  width: 100%;
  padding: 13px;
  background: #fff;
  color: #18591d;
  border: 1px solid #18591d;
  border-radius: 25px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  margin-top: 2px;
`;

const ErrorBox = styled.div`
  border-left: 4px solid #f44336;
  background: #ffebee;
  color: #c62828;
  padding: 12px 14px;
  border-radius: 8px;
  font-size: 0.95rem;
`;

const Input = styled.input`
  font-family: 'Poppins';
  width: 100%;
  padding: 10px;
  border-radius: 8px;
  font-size: 1.1rem;
  border: 1px solid #c4c4c4;

  &:focus {
    outline: none;
    border-color: #185faf;
    box-shadow: 0 0 10px rgba(24, 89, 29, 0.5);
  }
`;

const PasswordInputWrapper = styled.div`
  position: relative;
`;

const ForgotPasswordLink = styled.a`
  align-self: flex-end;
  font-size: 0.8rem;
  font-weight: bold;
  color: #0c0a0a;
  margin-bottom: 15px;
`;

const PasswordToggleIcon = styled.span`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  font-size: 20px;
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 13px;
  background: linear-gradient(45deg, #18591d, #00ff7f);
  color: #fff;
  border: none;
  border-radius: 25px;
  font-size: 1.2rem;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  font-family: 'Poppins';

  &::after {
    content: '';
    position: absolute;
    top: -50%;
    left: -75%;
    width: 200%;
    height: 200%;
    background: linear-gradient(120deg, transparent 30%, rgba(255, 255, 255, 0.3) 50%, transparent 70%);
    transform: rotate(25deg);
    animation: shimmer 3s linear infinite;
    z-index: 2;
  }

  &:hover {
    transform: scale(1.05);
    transition: all 0.3s ease;
  }
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.22);
`;